import React, { type HTMLAttributes, useState } from 'react';

import type {
  ImmutableEntity,
  ParagraphEntity,
} from '@uncinc/react-drupal-core/types';
import type { Hit, BaseHit } from 'instantsearch.js/es/types';
import type { PlainSearchParameters } from 'algoliasearch-helper';

import type { SearchSettingsConfig } from '@uncinc/react-searchkit/types';
import {
  SearchConfigure,
  SearchFacetRefinementCheckboxes,
  SearchFacets,
  SearchStats,
  SearchkitWrapper,
} from '@uncinc/react-searchkit';
import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { SearchGroupedResultsInfinite } from '../../../components';
import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../config/constants';

import './style.scss';

interface SearchResult extends Hit<BaseHit> {
  id: [string],
  type: [string],
  title: [string],
  body?: [string],
  path: [string],
  start_date: [number],
}

type ItemProps = {
  hit: SearchResult;
};

const Item: React.FC<ItemProps> = ({ hit }) => {
  const date = new Date(hit.start_date[0] * 1000);
  return (
    <article className="Search__item">
      <div>{`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`}</div>
      <h3 className="asH4"><a href={hit.path[0]}>{hit.title[0]}</a></h3>

      {hit.body && (
        <WysiwygField
          html={hit.body[0]}
        />
      )}
    </article>
  );
};

const searchSettings: SearchSettingsConfig = {
  search_attributes: ['title', 'body', 'target_audience'],
  result_attributes: ['title', 'nid', 'body', 'target_audience', 'type', 'path', 'date_bin', 'start_date', 'end_date'],
  facet_attributes: [
    {
      attribute: 'type',
      field: 'type',
      type: 'string',
    },
    {
      attribute: 'target_audience',
      field: 'target_audience',
      type: 'string',
    },
    {
      attribute: 'start_date',
      field: 'start_date',
      type: 'numeric',
    },
    {
      attribute: 'date_bin',
      field: 'date_bin',
      type: 'string',
    },
  ],
  sorting: {
    default: {
      field: 'start_date',
      order: 'asc',
    },
  },
  runtime_mappings: {
    start_date: {
      type: 'long',
      script: {
        source: `
          def firstUpcomingDate = null;
          def hasAnUpcomingDate = false;
          def today = new Date().getTime();

          for (int i = 0; i < params._source.start_date.length; i++) {
            long num = Long.parseLong(params._source.start_date[i].toString()) * 1000;
            Date date = new Date(num);

            if (date.getTime() > today) {
              if (!hasAnUpcomingDate) {
                firstUpcomingDate = date.getTime();
                hasAnUpcomingDate = true;
              } else if (date.getTime() < firstUpcomingDate) {
                firstUpcomingDate = date.getTime();
              }
            }
          }

          if (firstUpcomingDate !== null) {
            emit(firstUpcomingDate / 1000);
          }
        `,
      },
    },
    date_bin: {
      type: 'keyword',
      script: {
        source: `
          def occurences = new HashSet();

          def days = 24 * 3600 * 1000;
          Instant Currentdate = Instant.ofEpochMilli(new Date().getTime());
          LocalDateTime today = LocalDateTime.ofEpochSecond(
            Currentdate.getEpochSecond(), 0, ZoneOffset.ofHours(0)
          );

          Date todayStart = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue(),
              today.getDayOfMonth(), 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());
          Date tomorrowStart = new Date(todayStart.getTime() + (1 * days));
          Date tomorrowEnd = new Date(todayStart.getTime() + (2 * days));

          Date nextWeekendStart = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue(),
              today.getDayOfMonth() + (5 - today.getDayOfWeek().getValue() + 1), 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());
          Date nextWeekendEnd = new Date(nextWeekendStart.getTime() + (2 * days));

          Date nextWeekStart = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue(),
              today.getDayOfMonth() + (7 - today.getDayOfWeek().getValue() + 1), 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());
          Date nextWeekEnd = new Date(nextWeekStart.getTime() + (7 * days));

          Date thisMonthStart = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue(),
              1, 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());
          Date nextMonthStart = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue() + 1,
              1, 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());
          Date nextMonthEnd = new Date(
            ZonedDateTime.of(
              today.getYear(),
              today.getMonth().getValue() + 2,
              1, 0, 0, 0, 0, ZoneId.of('Z')).toInstant().toEpochMilli());

          for (int i = 0; i < params._source.start_date.length; i++) {
            long num = Long.parseLong(params._source.start_date[i].toString()) * 1000;
            Date date = new Date(num);
            def time = date.getTime();

            if (date.before(todayStart)) {
              continue;
            }

            if (time <= tomorrowStart.getTime()) {
              occurences.add('Vandaag');
            }

            if (time >= tomorrowStart.getTime() && time <= tomorrowEnd.getTime()) {
              occurences.add('Morgen');
            }

            if (time >= nextWeekendStart.getTime() && time <= nextWeekendEnd.getTime()) {
              occurences.add('Komend weekend');
            }

            if (time >= tomorrowEnd.getTime() && time <= nextWeekEnd.getTime()) {
              occurences.add('Volgende week');
            }

            if (date.before(nextMonthStart)) {
              occurences.add('Deze maand');
            }

            if (date.after(nextMonthStart) && date.before(nextMonthEnd)) {
              occurences.add('Volgende maand');
            }
          }

          def occ = occurences.toArray();
          for (int i = 0; i < occ.length; i++) {
            emit(occ[i]);
          }
        `,
      },
    },
  },
};

interface SearchParameters extends PlainSearchParameters {
  hitsPerPage?: number;
  filters?: string;
  page?: number;
}

const today = new Date();

const searchParameters = {
  filters: `start_date:[${today.getTime() / 1000} TO *]`,
} as SearchParameters;

interface ParagraphEventsFilteredProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<ParagraphEntity>;
}

const ParagraphEventsFiltered: React.FC<ParagraphEventsFilteredProps> = ({ id }) => {
  const [showRecurring, setShowRecurring] = useState(true);

  return (
    <div id={id} className="Paragraph ParagraphEventsFiltered">
      <SearchkitWrapper
        connection={{
          host: SEARCHKIT_HOST_URL,
        }}
        indexName={
          showRecurring ? APP_OPTIONS?.parameters.searchkitIndexAllEventsRecurring
            : APP_OPTIONS?.parameters.searchkitIndexAllEvents
        }
        routing
        hitsPerPage={100}
        searchSettings={searchSettings}
      >
        <SearchConfigure
          {...searchParameters}
        />
        <Container>
          <div className="row">
            <div className="MapSearch__header col-12 col-sm-4 col-lg-3">
              <label htmlFor="show_recurring">
                <input id="show_recurring" type="checkbox" checked={showRecurring} onChange={(e) => setShowRecurring(e.target.checked)} />
                &nbsp;
                Show recurring
              </label>
              <SearchFacets>
                <h3>Datum</h3>
                <SearchFacetRefinementCheckboxes attribute="date_bin" />
                <h3 className="mt-2">Type</h3>
                <SearchFacetRefinementCheckboxes attribute="type" />
                <h3 className="mt-2">Doelgroep</h3>
                <SearchFacetRefinementCheckboxes attribute="target_audience" />
              </SearchFacets>
            </div>

            <div className="MapSearch__results col-12 col-sm-8 col-lg-9">
              <SearchStats />
              <SearchGroupedResultsInfinite
                renderItem={(hit) => <Item hit={hit as SearchResult} />}
                autoScroll
              />
            </div>
          </div>
        </Container>
      </SearchkitWrapper>
    </div>
  );
};

export default ParagraphEventsFiltered;
